import React from 'react';
import { rubros } from './rubros';

/**
 * Navegación del sitio público.
 *
 * Estaba duplicada en Home.jsx, DemoPage.jsx y Plans.jsx: los mismos ~55
 * renglones con los mismos SVG de redes copiados tres veces, y por lo tanto
 * los mismos links rotos repetidos tres veces.
 *
 * IMPORTANTE — solo poner acá rutas que existan en App.js:
 *   /  ·  /auth/login  ·  /auth/register  ·  /legal/privacy  ·  /legal/terms
 *   /demo  ·  /admin/* (protegidas)
 *
 * El router tiene un catch-all `/:slug` que sirve la página pública de un
 * negocio, así que un link a una ruta inexistente NO da 404: intenta abrir un
 * negocio con ese slug y muestra una pantalla rota. Antes el footer apuntaba a
 * /caracteristicas, /precios, /nosotros, /contacto, /cookies, /privacidad y
 * /terminos, ninguna de las cuales existe.
 *
 * Las secciones de la landing se enlazan como `/#ancla` (con la barra) para
 * que funcionen también desde /demo y /admin/plans, no solo desde la home.
 */

/**
 * El WhatsApp de Aturno. Vive acá para que el número no quede escrito a mano en
 * cada lugar que lo necesite, que es como se queda viejo.
 *
 * Hoy lo usa sólo el overlay de "próximamente". El plan cotizado también lo
 * usaba, y ahora su botón lleva a sacar un turno en nuestra propia página
 * (`enlace` en data/planes.js): elegir un horario y quedar agendado es lo mismo
 * que ese plan le va a vender al que lo está mirando.
 */
export const WHATSAPP_ATURNO = '5491130032002';

export const linkDeWhatsApp = (mensaje) =>
  `https://wa.me/${WHATSAPP_ATURNO}?text=${encodeURIComponent(mensaje)}`;

const IconoInstagram = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const IconoLinkedIn = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export const footerData = {
  logo: <img src="/logo-aturno.png" alt="" className="h-7 w-7" />,
  brandName: 'Aturno',
  descripcion: 'Turnos online para tu negocio. Tus clientes reservan solos, vos te dedicás a trabajar.',
  // El cierre de la página. Antes el footer arrancaba directamente en columnas
  // de links: el último bloque era la FAQ y no había ninguna invitación a
  // empezar. Los textos repiten los del hero a propósito —misma promesa, mismas
  // condiciones— para no prometer dos cosas distintas en la misma página.
  cta: {
    id: 'empezar',
    titulo: 'Hacé crecer tu negocio con Aturno',
    bajada: 'Dejá de perder turnos por no atender el teléfono. Probalo 14 días gratis, sin tarjeta de crédito.',
    accion: { href: '/auth/register', label: 'Empezá tu prueba gratis' },
    secundaria: { href: '/demo', label: 'Ver Demo' },
  },
  // El footer los abre en una pestaña nueva con rel="noreferrer noopener"
  // (components/ui/footer.jsx), así que acá solo va la URL completa con https.
  //
  // LinkedIn apunta al perfil de la persona y no a una página de empresa: la
  // /company/aturno que estaba escrita acá no existe, o sea que el botón
  // llevaba al cartel de "esta página no está disponible" de LinkedIn.
  socialLinks: [
    { href: 'https://instagram.com/aturno', label: 'Instagram', icon: <IconoInstagram /> },
    { href: 'https://www.linkedin.com/in/matias-calo-50b7a8424', label: 'LinkedIn', icon: <IconoLinkedIn /> },
  ],
  columnas: [
    {
      titulo: 'Navegación',
      links: [
        /* #producto y no #caracteristicas: ese id era de Home.jsx, que dejó de
           ser "/" cuando la landing pasó a ser LandingDos. El enlace seguía
           existiendo y no llevaba a ningún lado —un ancla que no existe no
           falla, simplemente no hace nada—, que es la forma más silenciosa de
           romper un footer. */
        { href: '/#producto', label: 'Funcionalidades' },
        { href: '/#precios', label: 'Planes' },
        { href: '/#recursos', label: 'Preguntas frecuentes' },
        { href: '/demo', label: 'Ver demo' },
      ],
    },
    {
      titulo: 'Empezar',
      links: [
        { href: '/auth/register', label: 'Crear cuenta' },
        { href: '/auth/login', label: 'Iniciar sesión' },
      ],
    },
    {
      titulo: 'Legales',
      links: [
        { href: '/legal/privacy', label: 'Política de privacidad' },
        { href: '/legal/terms', label: 'Términos y condiciones' },
      ],
    },
  ],
  // Los rubros salen de rubros.js, la misma fuente que usan el onboarding y la
  // sección de rubros de la landing. Van como texto, no como links: no existen
  // páginas por rubro y no tiene sentido crear links rotos nuevos.
  rubros: rubros.map((r) => r.name),
  copyright: {
    text: `© ${new Date().getFullYear()} Aturno. Todos los derechos reservados.`,
    license: 'Hecho en Argentina',
  },
};
